// Logo rules shared by the sign-up stepper and dashboard Settings (via
// LogoUploader). One file is stored as `${businessId}.png` and the backend
// uses it for both wallets, so the rules are the stricter of the two:
//
//   Apple Wallet: pass images must be PNG; the logo sits in a 160 x 50 pt
//   slot (480 x 150 px at 3x) and the icon is 29 x 29 pt.
//   Google Wallet: programLogo should be PNG, 1:1 aspect ratio, at least
//   660 x 660 px, and is masked to a circle.
//
// A square image of at least 660 px satisfies both once the backend scales
// it, and JPGs are converted to PNG in the browser before upload.

export const MAX_LOGO_SIZE = 1.5 * 1024 * 1024; // 1.5 MB
export const MIN_LOGO_PX = 660;
// How far from square an image may be (660 x 661 is fine, 660 x 700 is not).
export const LOGO_ASPECT_TOLERANCE = 0.02;

const ACCEPTED_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/pjpeg"];
const ACCEPTED_EXTENSIONS = /\.(png|jpe?g)$/i;

// For the file input's `accept` attribute.
export const LOGO_ACCEPT_ATTR = "image/png,image/jpeg";

export const LOGO_REQUIREMENTS_SHORT = "Square PNG, JPG or JPEG, at least 660 × 660 px, max 1.5 MB";

export const LOGO_REQUIREMENTS_HINT =
  "Apple and Google Wallet show your logo in a small square or circle, so it must be square (1:1) and at least 660 × 660 pixels. PNG, JPG or JPEG, under 1.5 MB. A PNG with a transparent background blends into your card colour, and simple, high-contrast logos read best.";

// Some browsers leave `type` empty for files picked from certain apps, so
// the extension is the fallback.
export const isAcceptedLogoType = ({ type, name } = {}) => {
  const mime = String(type || "").toLowerCase();
  if (mime) return ACCEPTED_MIME_TYPES.includes(mime);
  return ACCEPTED_EXTENSIONS.test(String(name || ""));
};

// True when the stored file should be re-encoded to PNG (anything but PNG).
export const needsPngConversion = ({ type, name } = {}) => {
  const mime = String(type || "").toLowerCase();
  if (mime) return mime !== "image/png";
  return !/\.png$/i.test(String(name || ""));
};

export const isSquare = (width, height) => {
  const longest = Math.max(width, height);
  if (!(longest > 0)) return false;
  return Math.abs(width - height) / longest <= LOGO_ASPECT_TOLERANCE;
};

// Checks that can run before the image is decoded. Returns an error
// message, or "" when the file passes.
export const validateLogoFile = (file) => {
  if (!file) return "Please choose a PNG, JPG or JPEG image.";
  if (!isAcceptedLogoType(file)) return "Please choose a PNG, JPG or JPEG image.";
  if (file.size > MAX_LOGO_SIZE) {
    return "Logo must be under 1.5 MB. Please choose a smaller file.";
  }
  return "";
};

// Checks on the decoded pixel size. Returns an error message, or "" when
// the dimensions pass.
export const validateLogoDimensions = ({ width, height } = {}) => {
  if (!(width > 0 && height > 0)) {
    return "We couldn't read that image. Please choose a PNG, JPG or JPEG.";
  }
  const actual = `${width} × ${height} px`;
  if (width < MIN_LOGO_PX || height < MIN_LOGO_PX) {
    return `Logo must be at least ${MIN_LOGO_PX} × ${MIN_LOGO_PX} px - yours is ${actual}.`;
  }
  if (!isSquare(width, height)) {
    return `Logo must be square - yours is ${actual}.`;
  }
  return "";
};
